import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Plus, X, Loader, Leaf, ChevronDown } from 'lucide-react';
import { businessToday } from '../../utils/time';
import { useReadOnly } from '../../hooks/useReadOnly';
import StandingAddonRules, { ruleBody, StandingRule } from '../StandingAddonRules';
import { api } from '../../services/api';

/**
 * Lawn care → Add-ons — the extras that ride a mow (was the Mowing add-ons
 * page, Nov 2 2026: "revamp mowing add-ons, combine with treatments").
 *
 * Three things, top to bottom: what rides each running mowing schedule
 * (services/standingAddons — the same cadence picker and stop as the job
 * page), the extras sitting on visits in the next two weeks (take one off
 * here; put one on from the job page), and the list itself — what each
 * add-on costs, its crew minutes and what the crew is told.
 *
 * The owner, Sep 17 2026: "a new tab that just says mowing add-ons: bed
 * spraying, one-time weed treatments, one-time bush trimmings, anything, and
 * we can add it on to a mowing job."
 *
 * A treatment is priced off the Plan Cost Sheet by the yard's turf, so a big
 * lawn costs more than a small one without anybody doing sums. Everything else
 * has a price and crew minutes set here. Both go on a visit from the job page.
 */
const money = (n: any) => `$${Number(n || 0).toFixed(2)}`;

const dayWords = (d?: string | null) => d ? new Date(String(d).slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';
const DAYS_AHEAD = 14;

export default function AddonsTab() {
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { data: ov, isLoading } = useQuery(['addons-overview', DAYS_AHEAD], () => api.get('/mow-addons/overview', { params: { days: DAYS_AHEAD } }).then(r => r.data));
  const refresh = () => { qc.invalidateQueries('addons-overview'); qc.invalidateQueries('lawn-care-week'); qc.invalidateQueries('schedule-addons'); qc.invalidateQueries('job-addons'); };
  const drop = useMutation((addonId: number) => api.delete(`/mow-addons/job-addon/${addonId}`).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message); refresh(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not take that off'); },
  });
  if (isLoading) return <div className="py-16 flex justify-center"><Loader className="h-6 w-6 animate-spin text-brand-600" /></div>;
  const schedules: any[] = ov?.schedules || [];
  const upcoming: any[] = ov?.upcoming || [];
  const cadences: { key: string; label: string }[] = ov?.cadences || [];
  const byDay: Record<string, any[]> = {};
  for (const u of upcoming) (byDay[u.d] = byDay[u.d] || []).push(u);
  return (
    <div className="space-y-6">
      {/* ── What rides each running schedule ── */}
      <section className="space-y-2">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide">On a schedule · {schedules.length}</h2>
        {schedules.length === 0 ? (
          <p className="card p-4 text-sm text-gray-500">Nothing rides a mowing schedule yet. Open one of a yard's mows, add an extra and pick how often — it goes on every mow it is due on from there.</p>
        ) : schedules.map(s => <ScheduleCard key={s.id} s={s} cadences={cadences} readOnly={readOnly} onChanged={refresh} />)}
        <p className="text-[11px] text-gray-500">To start one on a yard that has none, open any of its mows (the job page) and pick how often. Changing a rule here counts from today; visits already done or billed are never touched.</p>
      </section>

      {/* ── Extras on visits in the next two weeks ── */}
      <section className="space-y-2">
        <h2 className="text-xs font-bold text-gray-500 uppercase tracking-wide">Coming up · next {DAYS_AHEAD} days · {upcoming.length}</h2>
        {upcoming.length === 0 ? (
          <p className="card p-4 text-sm text-gray-500">No extras on any visit in the next {DAYS_AHEAD} days.</p>
        ) : (
          <div className="card divide-y divide-gray-100">
            {Object.keys(byDay).sort().map(d => (
              <div key={d} className="px-3 py-2">
                <p className="text-xs font-semibold text-gray-700">{dayWords(d)}</p>
                <ul className="mt-1 space-y-1">
                  {byDay[d].map(u => (
                    <li key={u.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
                      <Link to={`/customers/${u.customer_id}`} className="font-medium text-gray-900 hover:underline">{u.customer_name}</Link>
                      <span className="text-gray-900">{u.name}</span>
                      <span className="text-gray-500">{money(u.price)}{u.minutes ? ` · ${u.minutes} min` : ''}</span>
                      <span className="text-xs text-gray-500">{u.rides ? 'on the mow' : 'a visit of its own'}{u.standing ? ' · by rule' : ''}{u.who ? ` · ${u.who}` : ''}{u.invoiced_at ? ' · billed' : ''}</span>
                      <Link to={`/jobs/${u.job_id}`} className="text-xs text-brand-600 hover:underline">job</Link>
                      {!u.invoiced_at && (
                        <button type="button" onClick={() => { if (window.confirm(`Take ${u.name} off ${u.customer_name}'s ${dayWords(u.d)} visit?`)) drop.mutate(u.id); }} disabled={readOnly || drop.isLoading} className="text-gray-300 hover:text-red-600" title="Take it off">
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      <Catalog readOnly={readOnly} />
    </div>
  );
}

/** One running mowing schedule: who, where, the next mow, what rides it (and the picker to add one). */
function ScheduleCard({ s, cadences, readOnly, onChanged }: { s: any; cadences: { key: string; label: string }[]; readOnly: boolean; onChanged: () => void }) {
  const [adding, setAdding] = useState(false);
  const rides: StandingRule[] = s.rules || [];
  const today = businessToday();
  const put = useMutation((list: StandingRule[]) => api.put(`/mow-addons/schedule/${s.id}`, { addons: ruleBody(list) }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d.message, { autoClose: 10000 }); setAdding(false); onChanged(); },
    onError: (e: any) => { toast.error(e.response?.data?.message || 'Could not change that'); },
  });
  return (
    <div className="card px-3 py-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Link to={`/customers/${s.customer_id}`} className="text-sm font-semibold text-gray-900 hover:underline break-words">{s.customer_name}</Link>
          <p className="text-xs text-gray-600 break-words">{s.address || 'no address'} · {s.frequency === 'biweekly' ? 'every other week' : s.frequency === 'weekly' ? 'weekly' : s.custom_interval_days ? `every ${s.custom_interval_days} days` : s.frequency}</p>
        </div>
        <span className={`text-xs whitespace-nowrap ${s.next_mow ? 'text-emerald-700' : 'text-gray-500'}`}>{s.next_mow ? `next mow ${dayWords(s.next_mow)}` : 'no mow booked'}</span>
      </div>
      <StandingAddonRules rides={rides} cadences={cadences} from={today} busy={put.isLoading} readOnly={readOnly} onChange={list => put.mutate(list)}
        title="Rides this schedule" note="Changing how often counts from today. Visits already done or billed are never touched." />
      {adding
        ? <AddStandingRule propertyId={s.property_id} cadences={cadences} have={rides.map(r => r.key)} saving={put.isLoading} onCancel={() => setAdding(false)}
            onAdd={entry => put.mutate([...rides.filter(a => a.key !== entry.key), { ...entry, name: entry.key, from: today } as StandingRule])} />
        : <button type="button" onClick={() => setAdding(true)} disabled={readOnly} className="mt-2 text-xs font-semibold text-brand-700 hover:underline disabled:opacity-50">+ Add one to this schedule</button>}
    </div>
  );
}

/** Pick an add-on off the list (priced for this yard), how often, how many times. Same body the job page sends. */
function AddStandingRule({ propertyId, cadences, have, saving, onAdd, onCancel }: {
  propertyId: number | null; cadences: { key: string; label: string }[]; have: string[]; saving: boolean;
  onAdd: (e: { key: string; cadence: string; price?: number; minutes?: number; times?: number }) => void; onCancel: () => void;
}) {
  const [pick, setPick] = useState('');
  const [price, setPrice] = useState('');
  const [minutes, setMinutes] = useState('');
  const [often, setOften] = useState(cadences[0]?.key || 'mow:1');
  const [times, setTimes] = useState('');
  const { data: menu } = useQuery(['addon-menu', propertyId, 'schedule'], () => api.get('/mow-addons', { params: { property_id: propertyId } }).then(r => r.data.addons as any[]));
  const chosen = (menu || []).find((m: any) => m.key === pick);
  return (
    <div className="mt-2 flex flex-wrap items-end gap-2">
      <label className="text-xs text-gray-600">Add
        <select value={pick} onChange={e => { setPick(e.target.value); const m = (menu || []).find((x: any) => x.key === e.target.value); setPrice(m ? String(m.price) : ''); setMinutes(m ? String(m.minutes) : ''); }} className="input mt-1 w-56">
          <option value="">Pick one…</option>
          {(menu || []).map((m: any) => <option key={m.key} value={m.key}>{m.name} — ${Number(m.price).toFixed(0)}{m.sheet_priced ? ' (sheet)' : ''}{have.includes(m.key) ? ' (already rides)' : ''}</option>)}
        </select>
      </label>
      <label className="text-xs text-gray-600">Price
        <input value={price} onChange={e => setPrice(e.target.value)} inputMode="decimal" className="input mt-1 w-24" />
      </label>
      <label className="text-xs text-gray-600">Minutes
        <input value={minutes} onChange={e => setMinutes(e.target.value)} inputMode="numeric" className="input mt-1 w-20" />
      </label>
      <label className="text-xs text-gray-600">How often
        <select value={often} onChange={e => setOften(e.target.value)} className="input mt-1 w-44">
          {cadences.map(c => <option key={c.key} value={c.key}>{c.label}, from today</option>)}
        </select>
      </label>
      <label className="text-xs text-gray-600">How many times
        <input value={times} onChange={e => setTimes(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="until stopped" className="input mt-1 w-28" />
      </label>
      <button type="button" onClick={() => onAdd({ key: pick, cadence: often, price: price === '' ? undefined : Number(price), minutes: minutes === '' ? undefined : Number(minutes), times: Number(times) > 0 ? Math.round(Number(times)) : undefined })}
        disabled={!pick || saving} className="btn-primary text-xs py-2 px-3 disabled:opacity-50">{saving ? 'Saving…' : 'Put it on the schedule'}</button>
      <button type="button" onClick={onCancel} className="text-xs text-gray-500 hover:underline">cancel</button>
      {chosen?.priced_from && <p className="w-full text-[11px] text-gray-500">{chosen.name}: {chosen.priced_from}.</p>}
    </div>
  );
}

// ── The list itself ──────────────────────────────────────────────────────────
/**
 * A treatment is priced off the Plan Cost Sheet by the yard's turf, so a big
 * lawn costs more than a small one without anybody doing sums. Everything else
 * has a price and crew minutes set here. Both go on a visit from the job page.
 */
function Catalog({ readOnly }: { readOnly: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery('mow-addons', () => api.get('/mow-addons', { params: { all: 1 } }).then(r => r.data.addons as any[]));

  const save = useMutation(({ key, patch }: { key: string; patch: any }) => api.put(`/mow-addons/${key}`, patch).then(r => r.data), {
    onSuccess: (d: any) => { qc.setQueryData('mow-addons', d.addons); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not save that'); },
  });
  const create = useMutation((body: any) => api.post('/mow-addons', body).then(r => r.data), {
    onSuccess: (d: any) => { qc.setQueryData('mow-addons', d.addons); setAdding(false); toast.success(d.message); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not add that'); },
  });
  const retire = useMutation((key: string) => api.delete(`/mow-addons/${key}`).then(r => r.data), {
    onSuccess: (d: any) => { qc.setQueryData('mow-addons', d.addons); toast.success(d.message); },
  });

  const addons = data || [];
  const live = addons.filter((a: any) => a.is_active).length;
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen(v => !v)} className="text-xs font-bold text-gray-500 uppercase tracking-wide flex items-center gap-1">
          The list · {live} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
        <button onClick={() => setAdding(true)} disabled={readOnly} className="btn-primary text-xs py-1.5 px-3 inline-flex items-center gap-1 disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Add one</button>
      </div>
      {open && (isLoading ? <p className="text-sm text-gray-500">Loading…</p> : (
        <div className="card divide-y divide-gray-100">
          {addons.map((a: any) => (
            <div key={a.key} className={`px-3 py-3 space-y-2 ${a.is_active ? '' : 'opacity-50'}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{a.name}</p>
                  <p className="text-xs text-gray-500">{a.blurb}</p>
                </div>
                {a.is_active
                  ? <button onClick={() => { if (window.confirm(`Take ${a.name} off the list? Visits that already have it are untouched.`)) retire.mutate(a.key); }} disabled={readOnly}
                      className="text-gray-300 hover:text-red-600" title="Take it off the list"><X className="h-4 w-4" /></button>
                  : <button onClick={() => save.mutate({ key: a.key, patch: { is_active: true } })} disabled={readOnly} className="text-xs font-semibold text-brand-700">Put back</button>}
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <label className="text-[11px] text-gray-600">Price
                  {a.sheet_priced ? (
                    <span className="block mt-1">
                      <span className="text-sm text-gray-900 inline-flex items-center gap-1"><Leaf className="h-3.5 w-3.5 text-emerald-600" /> {money(a.price)}</span>
                      <span className="block text-[11px] text-gray-500">off the <Link to="/treatments/sheet" className="underline">cost sheet</Link> · {a.priced_from}</span>
                    </span>
                  ) : (
                    <input type="number" defaultValue={a.set_price ?? ''} disabled={readOnly} onBlur={e => {
                      const v = e.target.value === '' ? null : Number(e.target.value);
                      if (v !== a.set_price) save.mutate({ key: a.key, patch: { price: v } });
                    }} className="block mt-1 w-24 bg-yellow-50 border border-yellow-300 rounded px-2 py-1 text-sm" />
                  )}
                </label>
                <label className="text-[11px] text-gray-600">Crew minutes
                  <input type="number" defaultValue={a.minutes} disabled={readOnly} onBlur={e => {
                    const v = Math.max(0, Number(e.target.value) || 0);
                    if (v !== a.minutes) save.mutate({ key: a.key, patch: { minutes: v } });
                  }} className="block mt-1 w-20 bg-yellow-50 border border-yellow-300 rounded px-2 py-1 text-sm text-right" />
                </label>
                <label className="text-[11px] text-gray-600 flex-1 min-w-[12rem]">What the crew is told
                  <input defaultValue={a.crew_note || ''} disabled={readOnly} onBlur={e => {
                    if (e.target.value !== (a.crew_note || '')) save.mutate({ key: a.key, patch: { crew_note: e.target.value } });
                  }} className="block mt-1 w-full bg-yellow-50 border border-yellow-300 rounded px-2 py-1 text-sm" />
                </label>
                <label className="text-[11px] text-gray-600 flex items-center gap-1.5 pb-1.5">
                  <input type="checkbox" checked={!!a.customer_can_ask} disabled={readOnly} onChange={e => save.mutate({ key: a.key, patch: { customer_can_ask: e.target.checked } })} /> Customers can ask
                </label>
              </div>
            </div>
          ))}
        </div>
      ))}
      {!open && <p className="text-[11px] text-gray-500">What each add-on costs, its crew minutes and what the crew is told. Customers can ask for the ticked ones from their own page.</p>}
      {adding && <NewAddon onClose={() => setAdding(false)} onSave={(body: any) => create.mutate(body)} saving={create.isLoading} />}
    </section>
  );
}

function NewAddon({ onClose, onSave, saving }: { onClose: () => void; onSave: (b: any) => void; saving: boolean }) {
  const [f, setF] = useState<any>({ name: '', blurb: '', kind: 'fixed', service: 'post', price: '', minutes: 15, crew_note: '', customer_can_ask: true });
  const set = (k: string) => (e: any) => setF((p: any) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-bold text-gray-900">New add-on</h3><button onClick={onClose}><X className="h-5 w-5 text-gray-500" /></button></div>
        <input className="input" placeholder="What is it called? (Bush trimming)" value={f.name} onChange={set('name')} autoFocus />
        <input className="input" placeholder="One line for the customer" value={f.blurb} onChange={set('blurb')} />
        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
          <button type="button" onClick={() => setF({ ...f, kind: 'fixed' })} className={`press flex-1 py-2 ${f.kind === 'fixed' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>A price I set</button>
          <button type="button" onClick={() => setF({ ...f, kind: 'treatment' })} className={`press flex-1 py-2 border-l border-gray-300 ${f.kind === 'treatment' ? 'bg-brand-600 text-black' : 'bg-white text-gray-700'}`}>Priced off the sheet</button>
        </div>
        {f.kind === 'treatment' ? (
          <label className="text-xs text-gray-600 block">Which treatment
            <select className="input mt-1" value={f.service} onChange={set('service')}>
              {[['post', 'Weed treatment'], ['fertilizer', 'Feed'], ['iron', 'Iron'], ['pre', 'Weed preventer'], ['grub', 'Grub'], ['fungus', 'Fungus'], ['insect', 'Insect'], ['moisture', 'Moisture hold'], ['opener', 'Soil opener'], ['core', 'Core aeration'], ['soiltest', 'Soil test']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <span className="block text-[11px] text-gray-500 mt-0.5">The price comes from the cost sheet, by the yard's turf.</span>
          </label>
        ) : (
          <label className="text-xs text-gray-600 block">Price
            <input type="number" className="input mt-1" value={f.price} onChange={set('price')} placeholder="65" />
          </label>
        )}
        <label className="text-xs text-gray-600 block">Crew minutes
          <input type="number" className="input mt-1" value={f.minutes} onChange={set('minutes')} />
        </label>
        <input className="input" placeholder="What the crew is told to do" value={f.crew_note} onChange={set('crew_note')} />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={f.customer_can_ask} onChange={set('customer_can_ask')} /> Customers can ask for this from their own page
        </label>
        <button onClick={() => onSave(f)} disabled={saving || !f.name.trim()} className="btn-primary w-full disabled:opacity-50">{saving ? 'Saving…' : 'Add it'}</button>
      </div>
    </div>
  );
}
